import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../../ResponsiveProvider';
import { theme, styles } from '../../style';
import { useProgressChecklist, ProgressTask } from './ProgressChecklistContext';
import { useActions } from '../../hooks/useActions';
import { 
  SvgTrophy, 
  SvgCheckmark, 
  SvgTarget,
  SvgStarFull,
  SvgClose
} from '../../icons/v1';
import { SvgCheckCircle1 } from '../../icons/v2';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Text } from '../common/Text';
import { View } from '../common/View';
import { ProgressCelebration } from './ProgressCelebration';

interface ProgressChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TaskItemProps {
  task: ProgressTask;
  onNavigate: (taskId: string, isCompleted: boolean) => void;
  index: number;
}

const TaskItem = React.memo(({ task, onNavigate, index }: TaskItemProps) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = useCallback(() => {
    // Allow navigation regardless of completion status, pass the completed status
    onNavigate(task.id, task.completed);
  }, [task.id, task.completed, onNavigate]);

  const getCategoryIcon = useCallback((category: ProgressTask['category']) => {
    switch (category) {
      case 'account':
        return '🏦';
      case 'transactions':
        return '💳';
      case 'budget':
        return '📊';
      case 'organization':
        return '📁';
      default:
        return '📋';
    }
  }, []);

  const getPriorityColor = useCallback((priority: ProgressTask['priority']) => {
    switch (priority) {
      case 'high':
        return '#FF5722';
      case 'medium':
        return '#FF9800';
      case 'low':
        return '#4CAF50';
      default:
        return theme.tableText;
    }
  }, []);

  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '12px',
        marginBottom: '8px',
        backgroundColor: task.completed ? '#E8F5E8' : theme.tableBackground,
        border: `2px solid ${task.completed ? '#4CAF50' : theme.tableBorder}`,
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        transform: isAnimating ? 'scale(1.02)' : 'scale(1)',
        boxShadow: task.completed 
          ? '0 4px 12px rgba(76, 175, 80, 0.2)' 
          : '0 2px 4px rgba(0, 0, 0, 0.1)',
        flexShrink: 0,
        minHeight: '60px',
        pointerEvents: 'auto',
        cursor: 'pointer',
      }}
      onClick={handleClick}
    >
      {/* Task Number and Icon */}
      <View
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: task.completed ? '#4CAF50' : theme.tableBackground,
          border: `2px solid ${task.completed ? '#4CAF50' : theme.tableBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '16px',
          flexShrink: 0,
          transition: 'all 0.3s ease',
        }}
      >
        {task.completed ? (
          <SvgCheckCircle1
            style={{
              width: '20px',
              height: '20px',
              color: 'white',
            }}
          />
        ) : (
          <Text
            style={{
              fontSize: '16px',
              fontWeight: 600,
              color: theme.tableText,
            }}
          >
            {index + 1}
          </Text>
        )}
      </View>

      {/* Task Content */}
      <View style={{ flex: 1, minWidth: 0 }}>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: '4px',
          }}
        >
          <Text
            style={{
              fontSize: '16px',
              fontWeight: 600,
              color: task.completed ? '#2E7D32' : theme.pageText,
              textDecoration: task.completed ? 'line-through' : 'none',
              marginRight: '8px',
            }}
          >
            {task.title}
          </Text>
          <Text
            style={{
              fontSize: '14px',
              marginRight: '8px',
            }}
          >
            {getCategoryIcon(task.category)}
          </Text>
          <View
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: getPriorityColor(task.priority),
            }}
          />
        </View>
        
        <Text
          style={{
            fontSize: '14px',
            color: task.completed ? '#4CAF50' : theme.tableText,
            lineHeight: 1.4,
            marginBottom: '8px',
          }}
        >
          {task.description}
        </Text>

        {task.completed && task.completedAt && (
          <Text
            style={{
              fontSize: '12px',
              color: '#4CAF50',
              fontStyle: 'italic',
            }}
          >
            Completed on {task.completedAt.toLocaleDateString()}
          </Text>
        )}
      </View>


    </View>
  );
});

export function ProgressChecklistModal({ isOpen, onClose }: ProgressChecklistModalProps) {
  const { state, hideChecklist, closeModal, hideTask } = useProgressChecklist();
  const { isNarrowWidth } = useResponsive();
  
  // Filter out hidden tasks from the display
  const visibleTasks = state.tasks.filter(task => !state.hiddenTasks?.includes(task.id));
  const navigate = useNavigate();
  const actions = useActions();
  const [showCelebration, setShowCelebration] = useState(false);
  const [showTaskCelebration, setShowTaskCelebration] = useState(false);
  const [completedTaskTitle, setCompletedTaskTitle] = useState<string>('');
  const [isModalReady, setIsModalReady] = useState(false);

  // Debug logging
  useEffect(() => {
  }, [isOpen]);

  // Handle modal ready state
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure modal is rendered before marking as ready
      const timer = setTimeout(() => {
        setIsModalReady(true);
        // Focus the modal content to ensure it's interactive
        const modalContent = document.querySelector('.task-list-container');
        if (modalContent) {
          (modalContent as HTMLElement).focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsModalReady(false);
    }
  }, [isOpen]);

  // Calculate progress based on visible tasks only
  const visibleCompletedCount = useMemo(() => 
    visibleTasks.filter(task => task.completed).length,
    [visibleTasks]
  );
  
  // Memoize calculations
  const progressPercentage = useMemo(() => 
    visibleTasks.length > 0 ? (visibleCompletedCount / visibleTasks.length) * 100 : 0, 
    [visibleCompletedCount, visibleTasks.length]
  );
  
  const isNearCompletion = useMemo(() => 
    progressPercentage >= 80, 
    [progressPercentage]
  );

  // Check if all visible tasks are completed
  const isAllCompleted = useMemo(() => 
    visibleTasks.length > 0 && visibleTasks.every(task => task.completed),
    [visibleTasks]
  );

  // Show celebration when all visible tasks are completed
  useEffect(() => {
    if (isAllCompleted && isOpen) {
      setShowCelebration(true);
      const timer = setTimeout(() => setShowCelebration(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isAllCompleted, isOpen]);



  const handleTaskNavigate = useCallback((taskId: string, isCompleted: boolean) => {
    // Close the modal first
    closeModal();
    onClose();

    // Navigate based on task ID
    switch (taskId) {
      case 'link-account':
        // Open the add-account modal
        actions.pushModal('add-account');
        break;
      case 'categorize-5-transactions':
        navigate('/accounts/budgeted');
        break;
      case 'review-5-transactions':
        navigate('/accounts/unreviewed');
        break;
      case 'batch-action':
        navigate('/accounts');
        break;
      case 'clear-uncategorized':
        // Navigate to uncategorized transactions page
        navigate('/accounts/uncategorized');
        break;
      case 'clear-unreviewed':
        navigate('/accounts/unreviewed');
        break;
      case 'create-rule':
        // Navigate to rules page
        navigate('/rules');
        break;
      case 'create-budget':
        // Navigate to planning page (not tracking/budget)
        navigate('/planning');
        break;
      case 'rename-budget':
        // Just open the rename modal - stay on current page
        actions.pushModal('rename-budget');
        break;
      case 'export-backup':
        // Navigate to settings for backup/export
        navigate('/settings');
        break;
      default:
        console.warn(`No navigation defined for task: ${taskId}`);
    }
  }, [navigate, actions, closeModal, onClose]);

  const handleHideChecklist = useCallback(() => {
    console.log('Hiding checklist...');
    hideChecklist();
    onClose();
  }, [hideChecklist, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Add keyboard shortcut to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleClose]);

  return (
    <>
      <Modal
        title={
          <View style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%',
            position: 'relative'
          }}>
            <View style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isAllCompleted ? (
                <SvgTrophy style={{ width: '24px', height: '24px', color: '#FFD700' }} />
              ) : isNearCompletion ? (
                <SvgStarFull style={{ width: '24px', height: '24px', color: '#FFD700' }} />
              ) : null}
              <Text style={{ fontSize: '20px', fontWeight: 600 }}>
                {isAllCompleted ? '🎉 Congratulations!' : 'Onboarding Checklist'}
              </Text>
            </View>
            <Button
              type="bare"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Close button clicked directly!');
                handleClose();
              }}
              style={{
                padding: '4px',
                fontSize: '12px',
                color: theme.tableText,
                pointerEvents: 'auto',
                cursor: 'pointer',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1000,
              }}
              title="Close (ESC)"
            >
              <SvgClose style={{ width: '16px', height: '16px' }} />
            </Button>
          </View>
        }
        isOpen={isOpen}
        onClose={handleClose}
        CloseButton={() => null}
        // size={{ width: isNarrowWidth ? '95vw' : '650px', height: isNarrowWidth ? '80vh' : '600px' }}
        // showOverlay={true}
        // showHeader={true}
        // noAnimation={false}
        // overlayStyle={{ zIndex: 2000 }}
        // contentStyle={{ 
        //   maxHeight: isNarrowWidth ? '80vh' : '600px',
        //   height: isNarrowWidth ? '80vh' : '600px',
        //   overflow: 'hidden',
        //   pointerEvents: 'auto',
        //   display: 'flex',
        //   alignItems: 'center',
        //   justifyContent: 'center',
        // }}
        // style={{
        //   pointerEvents: 'auto'
        // }}
      >
      <View style={{ 
        height: '100%', 
        maxHeight: '100%',
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        pointerEvents: 'auto',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        
        {/* Progress Header */}
        <View
          style={{
            backgroundColor: isNearCompletion ? '#E8F5E8' : '#E3F2FD',
            border: `2px solid ${isNearCompletion ? '#4CAF50' : '#2196F3'}`,
            borderRadius: '12px',
            padding: '15px',
            marginTop: '20px',
            marginBottom: '15px',
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          <Text
            style={{
              fontSize: '18px',
              fontWeight: 600,
              color: isNearCompletion ? '#2E7D32' : '#1976D2',
              marginBottom: '8px',
            }}
          >
            {isAllCompleted 
              ? 'You\'ve completed all onboarding tasks!' 
              : `${visibleCompletedCount} of ${visibleTasks.length} tasks completed`
            }
          </Text>
          
          <View
            style={{
              width: '100%',
              height: '12px',
              backgroundColor: theme.tableBackground,
              borderRadius: '6px',
              overflow: 'hidden',
              marginBottom: '8px',
            }}
          >
            <View
              style={{
                width: `${progressPercentage}%`,
                height: '100%',
                backgroundColor: isNearCompletion ? '#4CAF50' : '#2196F3',
                transition: 'width 0.5s ease, background-color 0.3s ease',
                position: 'relative',
              }}
            >
              {showCelebration && (
                <View
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, #4CAF50, #8BC34A, #4CAF50)',
                    animation: 'shimmer 1.5s infinite',
                  }}
                />
              )}
            </View>
          </View>
          
          <Text
            style={{
              fontSize: '14px',
              color: isNearCompletion ? '#4CAF50' : '#1976D2',
            }}
          >
            {Math.round(progressPercentage)}% Complete
          </Text>
        </View>

        {/* Tasks List */}
        <View
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}
        >
          <View
            className="task-list-container"
            tabIndex={0}
            style={{
              height: '100%',
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: '8px',
              position: 'relative',
              pointerEvents: 'auto',
              ...styles.lightScrollbar,
              WebkitOverflowScrolling: 'touch', // For smooth scrolling on iOS
              border: '1px solid transparent', // Force scrollbar to appear
            }}
          >
            {visibleTasks.map((task, index) => (
              <TaskItem
                key={task.id}
                task={task}
                onNavigate={handleTaskNavigate}
                index={index}
              />
            ))}
          </View>
        </View>

        {/* Action Buttons */}
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '15px',
            paddingTop: '15px',
            borderTop: `1px solid ${theme.tableBorder}`,
            flexShrink: 0,
            pointerEvents: 'auto',
          }}
        >
          {/* <Button
            type="bare"
            onClick={handleHideChecklist}
            style={{
              fontSize: '14px',
              color: theme.tableText,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            title="Hide the checklist permanently"
          >
            Hide Checklist
          </Button> */}
          
          {/* <View style={{ display: 'flex', gap: '12px' }}>
            <Button
              type="bare"
              onClick={handleClose}
              style={{
                fontSize: '14px',
                color: theme.tableText,
                cursor: 'pointer',
              }}
              title="Close the modal (ESC key also works)"
            >
              Close
            </Button>
            {isAllCompleted && (
              <Button
                type="primary"
                onClick={handleClose}
                style={{
                  backgroundColor: '#4CAF50',
                  fontSize: '14px',
                }}
              >
                🎉 Well Done!
              </Button>
            )}
          </View> */}
        </View>
      </View>

      <style>
        {`
          @keyframes shimmer {
            0% {
              background-position: -200px 0;
            }
            100% {
              background-position: calc(200px + 100%) 0;
            }
          }
          
          /* Custom scrollbar for task list */
          .task-list-container::-webkit-scrollbar {
            width: 12px;
          }
          
          .task-list-container::-webkit-scrollbar-track {
            background: #e0e0e0;
            border-radius: 6px;
            margin: 4px;
          }
          
          .task-list-container::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 6px;
            border: 2px solid #e0e0e0;
          }
          
          .task-list-container::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          
          /* Force scrollbar to always be visible */
          .task-list-container {
            scrollbar-width: thin;
            scrollbar-color: #888 #e0e0e0;
          }
        `}
      </style>
    </Modal>

    {/* Celebration Components */}
    <ProgressCelebration
      isVisible={showCelebration}
      onComplete={() => setShowCelebration(false)}
      isAllCompleted={true}
    />
    
    <ProgressCelebration
      isVisible={showTaskCelebration}
      onComplete={() => setShowTaskCelebration(false)}
      taskTitle={completedTaskTitle}
      isAllCompleted={false}
    />
  </>
  );
}
